import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Button } from '@mui/material';
import { Container, Box, CircularProgress, Typography, Alert, Snackbar, Paper } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

import 'dayjs/locale/pt-br';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

const VegetaisEstoque = () => {
    const [rowData, setRowData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    const { authenticatedFetch, logout } = useAuth();

    const API_URL = API_BASE_URL + '/api/vegetais-estoque/';
    const SAVE_API_URL = API_BASE_URL + '/api/vegetais-estoque-salvar-tabela/';

    const fetchVegetaisData = useCallback(async (date = null) => {
        setIsLoading(true);
        setError('');
        const payload = { data: date ? dayjs(date).toISOString() : null };

        try {
            const response = await authenticatedFetch(API_URL, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            const { dataSelecionada, dadosTabela } = data;

            if (dataSelecionada) {
                setSelectedDate(dayjs(dataSelecionada));
            }

            setRowData(
                (dadosTabela || []).map((row) => ({
                    ...row,
                    id: row.vegetal,
                    perda: row.perda ?? 0  // ← novo campo
                }))
            );

            if (dadosTabela?.length) {
                setSuccessMessage(`Dados carregados para ${dayjs(dataSelecionada).format('DD/MM/YYYY')}`);
            }
        } catch (err) {
            console.error('Erro ao buscar dados:', err);
            let errorMessage = 'Não foi possível carregar os dados. Tente novamente.';
            if (err.message.includes('Token')) {
                logout();
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.code === 'ECONNREFUSED') {
                errorMessage = 'Servidor indisponível. Verifique se o backend está rodando.';
            }
            alert(errorMessage);
            setRowData([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleSaveChanges = async () => {
        setIsUpdating(true);
        setError('');

        const payload = {
            data: dayjs(selectedDate).toISOString(),
            dados_tabela: rowData.map(({ id, estoque, saida, perda }) => ({
                vegetal: id,
                novo_estoque: estoque,
                nova_saida: saida,
                nova_perda: perda ?? 0  // ← novo campo
            }))
        };

        try {
            const response = await authenticatedFetch(SAVE_API_URL, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            setSuccessMessage('Estoque atualizado com sucesso!');
        } catch (err) {
            if (err.message.includes('Token')) {
                logout();
            } else {
                alert('Erro ao salvar alterações:' + err.message);
                setError(err.response?.data?.error || 'Não foi possível salvar as alterações. Tente novamente.');
            }
        } finally {
            setIsUpdating(false);
        }
    };

    useEffect(() => {
        if (!hasInitialLoad) {
            fetchVegetaisData(null);
            setHasInitialLoad(true);
        }
    }, [hasInitialLoad, fetchVegetaisData]);

    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
        if (newDate) fetchVegetaisData(newDate);
    };

    const [columnDefs] = useState([
        {
            field: 'vegetal',
            headerName: 'Vegetal',
            flex: 1,
            cellStyle: { fontWeight: 'bold' }
        },
        {
            field: 'estoque',
            headerName: 'Estoque Atual',
            flex: 1,
            editable: true,
            cellEditorParams: {
                min: 0,
                precision: 3
            },
            cellStyle: params => (
                params.value > 0
                    ? { backgroundColor: '#e8f5e8' }
                    : { backgroundColor: '#ffeaea' }
            )
        },
        // ── NOVA COLUNA ──────────────────────────────────────────────────────
        {
            field: 'perda',
            headerName: 'Perda',
            flex: 1,
            editable: true,
            cellEditorParams: {
                min: 0,
                precision: 3
            },
            valueFormatter: params =>
                params.value != null && params.value !== 0
                    ? Number(params.value).toFixed(3)
                    : '',
            cellStyle: params => (
                params.value > 0
                    ? { backgroundColor: '#fff3cd', color: '#856404', fontWeight: '600' }
                    : {}
            )
        },
        // ─────────────────────────────────────────────────────────────────────
        {
            field: 'saida',
            headerName: 'Saída',
            flex: 1,
            cellStyle: { color: '#666' },
            valueFormatter: params => (
                params.value != null ? Number(params.value).toFixed(3) : null
            )
        },
        {
            field: 'estoque_dia_anterior',
            hide: true
        },
        {
            field: 'compra',
            hide: true
        }
    ]);

    const onCellValueChanged = useCallback((params) => {
        const { data, newValue, colDef } = params;

        if (colDef.field === 'estoque' || colDef.field === 'perda') {
            // Resolve os dois valores relevantes independente de qual mudou
            const novoEstoque = colDef.field === 'estoque'
                ? parseFloat(newValue)
                : parseFloat(data.estoque ?? 0);

            const novaPerda = colDef.field === 'perda'
                ? parseFloat(newValue)
                : parseFloat(data.perda ?? 0);

            if (colDef.field === 'estoque' && (isNaN(novoEstoque) || novoEstoque < 0)) {
                setError('O estoque deve ser um número positivo.');
                params.api.refreshCells({ rowNodes: [params.node], force: true });
                return;
            }

            if (colDef.field === 'perda' && (isNaN(novaPerda) || novaPerda < 0)) {
                setError('A perda deve ser um número positivo.');
                params.api.refreshCells({ rowNodes: [params.node], force: true });
                return;
            }

            // Fórmula: Saída = Estoque anterior + Compra - Estoque atual - Perda
            const nova_saida =
                (parseFloat(data.estoque_dia_anterior) || 0)
                + (parseFloat(data.compra) || 0)
                - novoEstoque
                - (isNaN(novaPerda) ? 0 : novaPerda);

            setRowData(prevData =>
                prevData.map(row =>
                    row.id === data.id
                        ? {
                            ...row,
                            estoque: novoEstoque,
                            perda: isNaN(novaPerda) ? 0 : novaPerda,
                            saida: Math.max(parseFloat(nova_saida.toFixed(3)), 0)
                          }
                        : row
                )
            );
        }
    }, []);

    const defaultColDef = useMemo(() => ({
        minWidth: 120,
        filter: false,
        sortable: false,
        resizable: true,
    }), []);

    const gridOptions = useMemo(() => ({
        animateRows: true,
        suppressCellFocus: true,
        enableCellTextSelection: true,
    }), []);

    const handleCloseSnackbar = () => {
        setSuccessMessage('');
        setError('');
    };

    // Verifica se alguma linha tem perda lançada (para exibir o texto explicativo)
    const temPerda = rowData.some(r => (r.perda ?? 0) > 0);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <Box sx={{ flexGrow: 1, padding: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Date Picker */}
                <Box sx={{ width: '100%', maxWidth: 400 }}>
                    <DatePicker
                        label="Selecione a Data"
                        value={selectedDate}
                        onChange={handleDateChange}
                        sx={{ width: 150 }}
                        format="DD/MM/YYYY"
                    />
                </Box>

                {/* Mensagens de erro */}
                {error && (
                    <Alert severity="error" onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ flexGrow: 1, padding: 0, display: 'flex', flexDirection: 'column', gap: 3, width: 560 }}>

                    {isLoading || isUpdating ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>
                                {isLoading ? 'Carregando dados...' : 'Atualizando estoque...'}
                            </Typography>
                        </Box>
                    ) : rowData.length === 0 && !error ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                            <Typography color="textSecondary">
                                Nenhum dado encontrado. Selecione uma data ou verifique se há dados cadastrados.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            className="ag-theme-quartz"
                            sx={{ height: 158, width: '100%', maxWidth: 900 }}
                        >
                            <AgGridReact
                                enableBrowserTooltips={false}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                gridOptions={gridOptions}
                                onCellValueChanged={onCellValueChanged}
                                suppressRowClickSelection={true}
                                getRowId={(params) => params.data.id}
                            />
                        </Box>
                    )}

                    {/* Botão salvar + texto explicativo */}
                    {rowData.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveChanges}
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? <CircularProgress size={24} /> : 'Salvar Alterações'}
                                </Button>
                            </Box>

                            {/* ── Texto explicativo da lógica ─────────────────────────────── */}
                            <Paper
                                variant="outlined"
                                sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}
                            >
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Lógica de cálculo da Saída
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                    <strong>Saída</strong> = Estoque anterior + Compra − Estoque atual − Perda
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                    A <strong>perda</strong> é lançada no dia seguinte ao ocorrido. Ex.: se em 05/05 venceram
                                    2 kg de tomate, no dia 06/05 informa-se o estoque real (sem a perda) e o valor perdido
                                    no campo <em>Perda</em>. A saída do dia 05 já desconta a perda do dia 06.
                                </Typography>
                                {temPerda && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#856404', fontWeight: 600 }}>
                                        ⚠ Há perda lançada nesta data — a saída do dia anterior foi ajustada automaticamente.
                                    </Typography>
                                )}
                            </Paper>
                            {/* ──────────────────────────────────────────────────────────── */}
                        </Box>
                    )}

                    {/* Snackbar */}
                    <Snackbar
                        open={!!successMessage}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Alert severity="success" onClose={handleCloseSnackbar}>
                            {successMessage}
                        </Alert>
                    </Snackbar>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default VegetaisEstoque;